import { useEffect, useRef, useState, type FormEvent } from 'react';
import { useAuth } from '../hooks/useAuth';
import { motifLectureSeule } from '../lib/reglement';
import { useAnnee } from '../hooks/useAnnee';
import { useNavigate } from 'react-router-dom';
import { useDelai, useRessource } from '../hooks/useRessource';
import { CLASSE_SURVOL, useListeClavier } from '../hooks/useListeClavier';
import { ErreurApi, api } from '../lib/apiClient';
import {
  Badge,
  Champ,
  Chargement,
  EnTetePage,
  Erreur,
  Infobulle,
  Modale,
  Onglets,
  Tableau,
  Vide,
  type Colonne,
} from '../components/Elements';
import { trierLignes, type Tri } from '../lib/tri';
import { RemisesPage } from './RemisesPage';
import { BandeauMetriques } from '../components/BandeauMetriques';
import { BarreRecherche, type FiltreActif } from '../components/BarreRecherche';
import { dateExport, exporterTableau, montantExport } from '../lib/export';
import { capitaliser, formaterDate, formaterMois, formaterMontant, libelleJour } from '../lib/format';
import { CommunicationGroupee } from '../components/CommunicationGroupee';
import { ChoixMultiple } from '../components/ChoixMultiple';
import { estMineur } from '../lib/age';
import { corpsRelance, destinatairesRelance, lienGmailRelance, sujetRelance } from '../lib/relance';
import type {
  Adhesion,
  Creneau,
  FicheDestinataire,
  Inscription,
  LigneRapprochement,
  LigneVentilation,
  ModeReglement,
  Personne,
  Reglement,
  SyntheseReglements,
} from '../types';
import { BoutonExcel, BoutonGmail } from '../components/BoutonsIcone';

const MODES: { code: ModeReglement; libelle: string }[] = [
  { code: 'cheque', libelle: 'Chèque' },
  { code: 'virement', libelle: 'Virement' },
  { code: 'especes', libelle: 'Espèces' },
  { code: 'cheque_vacances', libelle: 'Chèque vacances' },
  { code: 'carte', libelle: 'Carte' },
];

const LIBELLE_MODE = Object.fromEntries(MODES.map((m) => [m.code, m.libelle]));

/** Un mois en toutes lettres depuis « 2025-09-01 ». */
const moisEnLettres = (date: string) => capitaliser(formaterMois(date));

/**
 * Poubelle monochrome, sur le modele d'IconeCivilite : viewBox 16, teinte
 * par currentColor pour suivre le survol du bouton. Le libelle vit sur le
 * bouton (title + aria-label) — le dupliquer ici le ferait annoncer deux
 * fois, d'ou aria-hidden.
 */
function IconePoubelle() {
  return (
    <svg
      viewBox="0 0 16 16"
      className="h-4 w-4"
      fill="currentColor"
      aria-hidden="true"
      focusable="false"
    >
      <rect x="6" y="1" width="4" height="1.5" rx="0.5" />
      <rect x="2.5" y="2.8" width="11" height="1.5" rx="0.75" />
      <path d="M4 5.3h8l-.55 8.2A1.6 1.6 0 0 1 9.85 15h-3.7a1.6 1.6 0 0 1-1.6-1.5L4 5.3z" />
    </svg>
  );
}

/**
 * Paiements (M5) — reglements, echeancier et rapprochement.
 *
 * Un reglement paie des INSCRIPTIONS, pas une famille : c'est ce qui
 * permet a un cheque unique de couvrir deux personnes sans ecriture de
 * contrepartie (RG-15), et au rapprochement de tomber juste (RG-21).
 */
export function PaiementsPage() {
  const { peut, entiteActive } = useAuth();
  const cle = entiteActive?.id ?? '';
  const annee = useAnnee();
  const [onglet, setOnglet] = useState('reglements');
  const [saisieOuverte, setSaisieOuverte] = useState(false);

  if (annee.chargement) return <Chargement />;
  if (!annee.annee) {
    return (
      <>
        <EnTetePage titre="Paiements" />
        <div className="carte p-6 text-sm text-encre-500">
          Aucune année scolaire sur cette entité.
        </div>
      </>
    );
  }

  const modifiable = annee.modifiable;

  return (
    <>
      <EnTetePage
        titre="Paiements"
        description="Un règlement couvre des inscriptions — pas une famille. Un même chèque peut donc payer pour deux personnes sans écriture de contrepartie."
        actions={
          <>
            {peut('reglement.creer') && modifiable && (
              <button
                type="button"
                className="bouton-principal"
                onClick={() => setSaisieOuverte(true)}
              >
                Saisir un règlement
              </button>
            )}
          </>
        }
      />

      <Onglets
        onglets={[
          { cle: 'reglements', libelle: 'Règlements' },
          { cle: 'echeancier', libelle: 'Échéancier' },
          { cle: 'rapprochement', libelle: 'Rapprochement' },
          { cle: 'remises', libelle: 'Remises' },
        ]}
        actif={onglet}
        onChanger={setOnglet}
      />

      {onglet === 'reglements' && (
        <ListeReglements anneeId={annee.anneeId} anneeLibelle={annee.annee.libelle} cle={cle} />
      )}
      {onglet === 'echeancier' && <Echeancier anneeId={annee.anneeId} cle={cle} />}
      {onglet === 'rapprochement' && <Rapprochement anneeId={annee.anneeId} cle={cle} />}
      {onglet === 'remises' && <RemisesPage anneeId={annee.anneeId} cle={cle} />}

      <FormulaireReglement
        ouverte={saisieOuverte}
        anneeId={annee.anneeId}
        anneeLibelle={annee.annee.libelle}
        onFermer={() => setSaisieOuverte(false)}
        onEnregistre={() => {
          setSaisieOuverte(false);
          // Le plus simple pour rafraichir les trois onglets a la fois.
          setOnglet((actuel) => actuel);
          window.location.reload();
        }}
      />
    </>
  );
}

/** Un statut derive s'affiche en francais : « en_cours » n'est pas un libelle. */
const LIBELLE_STATUT: Record<string, string> = {
  prevu: 'prévu',
  en_cours: 'en cours',
  encaisse: 'encaissé',
  incident: 'incident',
};

function ListeReglements({
  anneeId,
  anneeLibelle,
  cle,
}: {
  anneeId: string;
  anneeLibelle: string;
  cle: string;
}) {
  const { peut } = useAuth();
  const [saisie, setSaisie] = useState('');
  const [mode, setMode] = useState('');
  const [statut, setStatut] = useState('');
  const [mois, setMois] = useState('');
  const recherche = useDelai(saisie);

  const reglements = useRessource(
    () =>
      api.get<Reglement[]>('/api/reglements', {
        anneeId,
        recherche: recherche || undefined,
        mode: mode || undefined,
        statut: statut || undefined,
        // Un mois d'encaissement se borne par ses deux extremites : le
        // serveur filtre sur un intervalle, pas sur un libelle.
        du: mois ? `${mois}-01` : undefined,
        au: mois ? `${mois}-28` : undefined,
        limite: 2000,
      }),
    [cle, anneeId, recherche, mode, statut, mois],
  );

  const liste = reglements.donnees ?? [];
  const total = liste.reduce((somme, r) => somme + r.montant_net, 0);

  const synthese = useRessource(
    () => api.get<SyntheseReglements>('/api/reglements/synthese', { anneeId }),
    [cle, anneeId],
  );

  // SUPPRESSION — quatre cas, tous tenus aussi par le serveur.
  //
  // Un reglement rattache a une remise ne se supprime pas : on l'en retire
  // d'abord. Un reglement en incident non plus, et par personne — la base
  // le tient en ON DELETE RESTRICT sur incident_reglement, ce qui est RG-16
  // rendu structurel. Un reglement prevu n'a rien constate en banque, la
  // Direction le supprime. Au-dela, effacer un encaissement supprime une
  // trace bancaire reelle : permission dediee, que seul le super_admin
  // detient. L'ecran n'ajoute rien a la regle, il l'affiche.
  const peutSupprimer = (reglement: Reglement): boolean => {
    if (reglement.remise_id !== null) return false;
    if (reglement.statut === 'incident') return false;
    if (reglement.statut === 'prevu') return peut('reglement.supprimer');
    return peut('reglement.supprimer') && peut('reglement.supprimer_encaisse');
  };

  const [enModification, setEnModification] = useState<Reglement | null>(null);

  const supprimer = async (reglement: Reglement) => {
    const identite = reglement.beneficiaires ?? 'sans bénéficiaire';
    if (
      !window.confirm(
        `Supprimer ce règlement de ${formaterMontant(reglement.montant_net)} (${identite}) ?

` +
          `Cette action ne s’annule pas.`,
      )
    ) {
      return;
    }
    try {
      await api.delete(`/api/reglements/${reglement.id}`);
      reglements.recharger();
      synthese.recharger();
    } catch (cause) {
      window.alert(cause instanceof ErreurApi ? cause.message : 'Suppression impossible.');
    }
  };

  const filtresActifs: FiltreActif[] = [
    recherche && { cle: 'recherche', libelle: `« ${recherche} »`, onRetirer: () => setSaisie('') },
    mode && {
      cle: 'mode',
      libelle: MODES.find((m) => m.code === mode)?.libelle ?? mode,
      onRetirer: () => setMode(''),
    },
    statut && {
      cle: 'statut',
      libelle: statut === 'prevu' ? 'Prévu' : statut === 'encaisse' ? 'Encaissé' : 'Incident',
      onRetirer: () => setStatut(''),
    },
    mois && { cle: 'mois', libelle: mois, onRetirer: () => setMois('') },
  ].filter(Boolean) as FiltreActif[];

  // Les douze mois de l'annee, tires des reglements eux-memes : proposer
  // un mois vide ne servirait qu'a afficher une liste vide.
  const moisDisponibles = [...new Set(liste.map((r) => r.date_encaissement.slice(0, 7)))].sort();

  const colonnes: Colonne<Reglement>[] = [
    {
      cle: 'beneficiaires',
      entete: 'Adhérent',
      rendu: (r) => <span className="font-medium">{r.beneficiaires ?? '—'}</span>,
    },
    {
      cle: 'mode',
      entete: 'Moyen de paiement',
      rendu: (r) => (
        <div>
          <div className="flex flex-wrap items-center gap-2">
            <span>{LIBELLE_MODE[r.mode] ?? r.mode}</span>
            {r.numero_cheque && (
              <span className="tabulaire text-xs text-encre-500">
                n° {r.numero_cheque}
                {r.banque ? ` · ${r.banque}` : ''}
              </span>
            )}
            {/* RG-12 : le doublon est un fait signale, pas une erreur. */}
            {r.doublon_confirme && <Badge ton="alerte">n° en doublon</Badge>}
          </div>
          {/* RG-11 : l'emetteur qualifie le moyen de paiement, pas
              l'adherent — il en differe souvent (un parent, un tiers). */}
          {r.emetteur_nom && r.emetteur_nom !== r.beneficiaires && (
            <div className="text-xs text-encre-500">émis par {r.emetteur_nom}</div>
          )}
          {/* UN REMBOURSEMENT est un reglement negatif (2026-10-09) : le
              signe le dit, la pastille le fait voir d'un coup d'oeil. */}
          {r.montant < 0 && (
            <div className="mt-1">
              <Badge ton="danger">remboursement</Badge>
            </div>
          )}
        </div>
      ),
    },
    {
      cle: 'date',
      entete: 'Encaissement',
      rendu: (r) => (
        <div>
          <div className="tabulaire">{formaterDate(r.date_encaissement)}</div>
          {r.date_encaissee && r.date_encaissee !== r.date_encaissement && (
            <div className="text-xs text-encre-400">réel : {formaterDate(r.date_encaissee)}</div>
          )}
        </div>
      ),
    },
    {
      cle: 'montant',
      entete: 'Montant',
      classe: 'text-right',
      rendu: (r) => (
        <div>
          <span className={`tabulaire font-medium ${r.montant < 0 ? 'text-red-700' : ''}`}>
            {formaterMontant(r.montant_net)}
          </span>
          {r.montant_rembourse > 0 && (
            <div className="tabulaire text-xs text-amber-700">
              dont −{formaterMontant(r.montant_rembourse)} remboursés
            </div>
          )}
          {/* Un versement non entierement affecte : trop-percu, ou nom sans
              inscription. C'est ce que le rapprochement ira chercher.
              En TAG et non en texte (2026-09-01) : la saisie autorise
              desormais d'enregistrer une affectation partielle, ce cas
              cesse donc d'etre exceptionnel et doit se reperer d'un coup
              d'oeil dans la liste. */}
          {r.montant_ventile < r.montant_net && (
            <div className="mt-1">
              <Badge ton="alerte">
                reste à allouer {formaterMontant(r.montant_net - r.montant_ventile)}
              </Badge>
            </div>
          )}
        </div>
      ),
    },
    {
      cle: 'statut',
      entete: 'Statut',
      rendu: (r) =>
        r.statut === 'encaisse' ? (
          <span className="text-xs text-encre-400">encaissé</span>
        ) : (
          <Badge
            ton={r.statut === 'incident' ? 'danger' : r.statut === 'en_cours' ? 'neutre' : 'alerte'}
          >
            {LIBELLE_STATUT[r.statut] ?? r.statut}
          </Badge>
        ),
    },
    ...(peut('reglement.supprimer')
      ? [
          {
            cle: 'actions',
            entete: '',
            classe: 'text-right',
            rendu: (r: Reglement) =>
              peutSupprimer(r) ? (
                <Infobulle texte="Supprimer" className="inline-flex">
                  <button
                    type="button"
                    className="rounded px-1 text-encre-400 hover:bg-encre-100 hover:text-red-700"
                    aria-label="Supprimer"
                    onClick={(evenement) => {
                      evenement.stopPropagation();
                      void supprimer(r);
                    }}
                  >
                    <IconePoubelle />
                  </button>
                </Infobulle>
              ) : null,
          },
        ]
      : []),
  ];

  return (
    <>
      {synthese.donnees && (
        <BandeauMetriques
          metriques={[
            {
              libelle: 'Montant encaissé',
              valeur: formaterMontant(synthese.donnees.encaisse),
              detail: `${synthese.donnees.nombre_encaisses} règlements`,
              ton: 'succes',
            },
            {
              libelle: 'Prévu',
              valeur: formaterMontant(synthese.donnees.prevu),
              detail: `${synthese.donnees.nombre_prevus} chèques non déposés`,
              action: () => setStatut('prevu'),
            },
            {
              libelle: 'CA encaissé',
              valeur: `${String(synthese.donnees.taux_encaissement).replace('.', ',')} %`,
              detail: `${synthese.donnees.cheques_deposes} chèques · ${synthese.donnees.remises_deposees} remises déposées`,
            },
            {
              libelle: 'Reste à encaisser',
              valeur: formaterMontant(synthese.donnees.reste),
              detail: `${synthese.donnees.debiteurs} adhérent(s)`,
              ton: 'alerte',
            },
          ]}
        />
      )}

      <BarreRecherche
        recherche={saisie}
        onChercher={setSaisie}
        placeholder="Nom, émetteur ou n° de chèque…"
        actifs={filtresActifs}
        onToutEffacer={() => {
          setSaisie('');
          setMode('');
          setStatut('');
          setMois('');
        }}
        resume={
          <>
            <span className="tabulaire font-medium text-encre-800">{liste.length}</span>{' '}
            règlement(s)
            {synthese.donnees
              ? ` sur ${synthese.donnees.nombre_encaisses + synthese.donnees.nombre_prevus}`
              : ''}{' '}
            — <span className="tabulaire">{formaterMontant(total)}</span>
          </>
        }
        onExporter={
          liste.length
            ? () =>
                exporterTableau(
                  'reglements',
                  [
                    'Adhérent',
                    'Moyen',
                    'Banque',
                    'N° chèque',
                    'Émetteur',
                    'Encaissement',
                    'Montant',
                    'Statut',
                  ],
                  liste.map((r) => [
                    r.beneficiaires ?? '',
                    MODES.find((m) => m.code === r.mode)?.libelle ?? r.mode,
                    r.banque ?? '',
                    r.numero_cheque ?? '',
                    r.emetteur_nom ?? '',
                    dateExport(r.date_encaissement),
                    montantExport(r.montant_net),
                    r.statut,
                  ]),
                )
            : undefined
        }
        filtres={
          <>
            <select className="champ w-auto" value={mode} onChange={(e) => setMode(e.target.value)}>
              <option value="">Tous les moyens</option>
              {MODES.map((m) => (
                <option key={m.code} value={m.code}>
                  {m.libelle}
                </option>
              ))}
            </select>

            <select className="champ w-auto" value={mois} onChange={(e) => setMois(e.target.value)}>
              <option value="">Tous les mois</option>
              {moisDisponibles.map((m) => (
                <option key={m} value={m}>
                  {m}
                </option>
              ))}
            </select>

            <select
              className="champ w-auto"
              value={statut}
              onChange={(e) => setStatut(e.target.value)}
            >
              <option value="">Tous les statuts</option>
              <option value="prevu">Prévu</option>
              <option value="en_cours">En cours</option>
              <option value="encaisse">Encaissé</option>
              <option value="incident">Incident</option>
            </select>
          </>
        }
      />

      {reglements.erreur && (
        <Erreur message={reglements.erreur} onReessayer={reglements.recharger} />
      )}
      {reglements.chargement ? (
        <Chargement />
      ) : (
        <div className="carte">
          <Tableau
            colonnes={colonnes}
            lignes={liste}
            cleDe={(r) => r.id}
            messageVide="Aucun règlement ne correspond."
            // Toute ligne s'ouvre (2026-10-07) : verrouillee, le formulaire
            // s'affiche en lecture seule et dit pourquoi.
            onLigneCliquee={setEnModification}
          />
        </div>
      )}

      <FormulaireReglement
        ouverte={enModification !== null}
        anneeId={anneeId}
        anneeLibelle={anneeLibelle}
        reglement={enModification}
        onFermer={() => setEnModification(null)}
        onEnregistre={() => {
          setEnModification(null);
          reglements.recharger();
          synthese.recharger();
        }}
      />
    </>
  );
}

/**
 * L'echeancier : ce qui doit etre encaisse, mois par mois.
 *
 * C'est la vue qui justifie RG-13 — les dates normalisees au 1er. Les
 * cheques sont collectes a l'inscription et endosses au mois le mois :
 * sans regroupement mensuel, on ne sait pas ce qu'il faut deposer.
 */
function Echeancier({ anneeId, cle }: { anneeId: string; cle: string }) {
  const reglements = useRessource(
    () => api.get<Reglement[]>('/api/reglements', { anneeId, limite: 2000 }),
    [cle, anneeId],
  );

  if (reglements.chargement) return <Chargement />;
  if (reglements.erreur) {
    return <Erreur message={reglements.erreur} onReessayer={reglements.recharger} />;
  }

  const liste = reglements.donnees ?? [];
  if (liste.length === 0) return <Vide message="Aucun règlement sur cette année." />;

  const parMois = new Map<string, Reglement[]>();
  for (const r of liste) {
    const mois = r.date_encaissement.slice(0, 7);
    parMois.set(mois, [...(parMois.get(mois) ?? []), r]);
  }
  const mois = [...parMois.keys()].sort();
  const maximum = Math.max(
    ...mois.map((m) => (parMois.get(m) ?? []).reduce((s, r) => s + r.montant_net, 0)),
  );

  return (
    <div className="carte">
      <table className="min-w-full divide-y divide-encre-200 text-sm">
        <thead className="bg-encre-50">
          <tr>
            <th className="px-4 py-3 text-left text-xs font-semibold uppercase tracking-wide text-encre-500">
              Mois
            </th>
            <th className="px-4 py-3 text-right text-xs font-semibold uppercase tracking-wide text-encre-500">
              Règlements
            </th>
            <th className="px-4 py-3 text-right text-xs font-semibold uppercase tracking-wide text-encre-500">
              Chèques
            </th>
            <th className="px-4 py-3 text-right text-xs font-semibold uppercase tracking-wide text-encre-500">
              Montant
            </th>
            <th className="w-1/3 px-4 py-3" />
          </tr>
        </thead>
        <tbody className="divide-y divide-encre-100 bg-white">
          {mois.map((m) => {
            const lignes = parMois.get(m) ?? [];
            const montant = lignes.reduce((s, r) => s + r.montant_net, 0);
            const cheques = lignes.filter((r) => r.mode === 'cheque').length;
            return (
              <tr key={m}>
                <td className="px-4 py-3 font-medium">{moisEnLettres(`${m}-01`)}</td>
                <td className="tabulaire px-4 py-3 text-right">{lignes.length}</td>
                <td className="tabulaire px-4 py-3 text-right text-encre-500">{cheques}</td>
                <td className="tabulaire px-4 py-3 text-right font-medium">
                  {formaterMontant(montant)}
                </td>
                <td className="px-4 py-3">
                  <div
                    className="h-2 rounded-full bg-encre-800"
                    style={{ width: `${Math.max((montant / maximum) * 100, 1)}%` }}
                  />
                </td>
              </tr>
            );
          })}
        </tbody>
        <tfoot className="border-t-2 border-encre-300 bg-encre-50">
          <tr>
            <td className="px-4 py-3 font-semibold">Total</td>
            <td className="tabulaire px-4 py-3 text-right font-semibold">{liste.length}</td>
            <td className="tabulaire px-4 py-3 text-right text-encre-500">
              {liste.filter((r) => r.mode === 'cheque').length}
            </td>
            <td className="tabulaire px-4 py-3 text-right font-semibold">
              {formaterMontant(liste.reduce((s, r) => s + r.montant_net, 0))}
            </td>
            <td />
          </tr>
        </tfoot>
      </table>
    </div>
  );
}

/**
 * RG-21 : somme des reglements contre somme des montants dus.
 *
 * Le rapprochement est PAR PERSONNE, faute de famille (ADR-0011). Un
 * ecart negatif est un reste a payer, un ecart positif un trop-percu.
 */
type CleFiltreRapprochement = 'activites' | 'jours' | 'professeurs';
const CLES_RAPPROCHEMENT: CleFiltreRapprochement[] = ['activites', 'jours', 'professeurs'];
const LIBELLE_TYPE_MEMBRE: Record<LigneRapprochement['type_membre'], string> = {
  actif: 'Membre actif',
  usager: 'Membre usager',
  inactif: 'Inactif',
};

function Rapprochement({ anneeId, cle }: { anneeId: string; cle: string }) {
  const { peut, session, entiteActive } = useAuth();
  const [seulementEcarts, setSeulementEcarts] = useState(true);
  // RELANCER (2026-10-17) : un brouillon Gmail prerempli pour la ligne,
  // adresse aux parents d'un mineur ou a l'adulte lui-meme. Les adresses
  // viennent du calcul de destinataires, journalise comme la communication
  // groupee ; le texte est celui du modele valide par Georges.
  const [relanceEnCours, setRelanceEnCours] = useState<string | null>(null);
  const [avisRelance, setAvisRelance] = useState<string | null>(null);
  const relancer = async (l: LigneRapprochement) => {
    setAvisRelance(null);
    setRelanceEnCours(l.personne_id);
    try {
      const reponse = await api.post<{
        fiches: FicheDestinataire[];
        compte: { titulaire: string; iban: string; bic: string | null } | null;
      }>('/api/messages/destinataires', { personneIds: [l.personne_id] });
      const fiche = reponse.fiches[0];
      const mineur = estMineur(l.date_naissance);
      const destinataires = fiche ? destinatairesRelance(fiche, mineur) : [];
      if (destinataires.length === 0) {
        setAvisRelance(
          `${l.nom.toUpperCase()} ${l.prenom} : aucune adresse email sur la fiche ni chez ses contacts. Complétez la fiche avant de relancer.`,
        );
        return;
      }
      const donnees = {
        ecole: capitaliser((entiteActive?.code ?? 'Artistic').toLowerCase()),
        raisonSociale: entiteActive?.raisonSociale ?? 'l’association',
        prenom: l.prenom,
        nom: l.nom,
        resteDu: -l.ecart,
        cotisationNonSoldee: l.du_adhesion > 0 && l.adhesion_reglee < l.du_adhesion,
        mineur,
        compte: reponse.compte,
      };
      window.open(
        lienGmailRelance(session?.utilisateur.email ?? '', destinataires, sujetRelance(donnees), corpsRelance(donnees)),
        '_blank',
        'noopener',
      );
    } catch (cause) {
      setAvisRelance(cause instanceof ErreurApi ? cause.message : 'Relance impossible.');
    } finally {
      setRelanceEnCours(null);
    }
  };
  // ACTIVITES, JOURS, PROFESSEURS (2026-10-13), a choix multiples, lus par
  // le serveur sur la meme inscription de l'annee — comme sur Adherents
  // et Inscriptions —, plus le TYPE de membre de l'annee.
  const [filtres, setFiltres] = useState<Record<CleFiltreRapprochement, string[]>>({
    activites: [],
    jours: [],
    professeurs: [],
  });
  const [types, setTypes] = useState<string[]>([]);
  // Tri sur toutes les colonnes, par adherent a l'ouverture (2026-09-29).
  // L'etat vit ici et non dans le tableau : l'export doit sortir les
  // lignes dans l'ordre affiche.
  const [tri, setTri] = useState<Tri>({ cle: 'personne', sens: 'asc' });
  // « Ecrire a la selection » : les adherents des lignes affichees.
  const [ecrireA, setEcrireA] = useState<string[] | null>(null);
  const naviguer = useNavigate();

  const lignes = useRessource(
    () =>
      api.get<LigneRapprochement[]>('/api/rapprochement', {
        anneeId,
        seulementEcarts,
        activites: filtres.activites,
        jours: filtres.jours,
        professeurs: filtres.professeurs,
        types,
      }),
    [cle, anneeId, seulementEcarts, JSON.stringify(filtres), types.join()],
  );

  // LES TROIS LISTES SE DEDUISENT LES UNES DES AUTRES, sur la grille des
  // creneaux de l'annee — la mecanique de la liste des creneaux : chaque
  // liste se calcule depuis les deux autres, et un choix rendu vain par
  // un autre s'efface. A choix multiples, un creneau passe une liste des
  // qu'il repond a l'UNE des valeurs cochees.
  const creneaux = useRessource(() => api.get<Creneau[]>('/api/creneaux', { anneeId }), [cle, anneeId]);
  const tousCreneaux = creneaux.donnees ?? [];
  const passe = (c: Creneau, f: typeof filtres, sauf: CleFiltreRapprochement | null = null) =>
    (sauf === 'activites' || f.activites.length === 0 || f.activites.includes(c.activite_id)) &&
    (sauf === 'jours' || f.jours.length === 0 || f.jours.includes(String(c.jour))) &&
    (sauf === 'professeurs' ||
      f.professeurs.length === 0 ||
      c.encadrants.some((e) => f.professeurs.includes(e.personnel_id)));
  const poserFiltre = (cleFiltre: CleFiltreRapprochement, valeurs: string[]) => {
    const suite = { ...filtres, [cleFiltre]: valeurs };
    // Dans chaque AUTRE liste, une valeur qu'aucun creneau ne porte plus
    // s'efface ; la liste qu'on vient de toucher est prise telle quelle.
    for (const autre of CLES_RAPPROCHEMENT) {
      if (autre === cleFiltre || suite[autre].length === 0) continue;
      suite[autre] = suite[autre].filter((valeur) =>
        tousCreneaux.some((c) => passe(c, { ...suite, [autre]: [valeur] })),
      );
    }
    setFiltres(suite);
  };
  const options = (sauf: CleFiltreRapprochement) => tousCreneaux.filter((c) => passe(c, filtres, sauf));
  const activitesProposees = [
    ...new Map(options('activites').map((c) => [c.activite_id, c.activite_libelle])),
  ].sort((a, b) => a[1].localeCompare(b[1]));
  const joursProposes = [...new Set(options('jours').map((c) => c.jour))].sort((a, b) => a - b);
  const encadrantsProposes = [
    ...new Map(
      options('professeurs').flatMap((c) => c.encadrants.map((e) => [e.personnel_id, e] as const)),
    ).values(),
  ].sort((a, b) => a.prenom_usuel.localeCompare(b.prenom_usuel));
  const libelleEncadrant = (e: (typeof encadrantsProposes)[number]) =>
    encadrantsProposes.filter((x) => x.prenom_usuel === e.prenom_usuel).length > 1
      ? `${e.prenom_usuel} ${e.nom ?? ''}`.trim()
      : e.prenom_usuel;

  if (lignes.chargement) return <Chargement />;
  if (lignes.erreur) return <Erreur message={lignes.erreur} onReessayer={lignes.recharger} />;

  const liste = lignes.donnees ?? [];
  // Le total du des lignes AFFICHEES (2026-10-13) : la somme des restes,
  // signee comme eux — negatif, il reste a payer.
  const totalDu = liste.reduce((somme, l) => somme + l.ecart, 0);

  const colonnes: Colonne<LigneRapprochement>[] = [
    {
      cle: 'personne',
      entete: 'Adhérent',
      valeurTri: (l) => `${l.nom} ${l.prenom}`,
      // Les activites en pastilles a cote du nom (2026-10-13) : c'est par
      // le cours qu'on sait a qui parler d'un reste du.
      rendu: (l) => (
        <span className="flex flex-wrap items-center gap-1.5">
          <span className="font-medium">
            {l.nom.toUpperCase()} {l.prenom}
          </span>
          {l.activites.map((activite) => (
            <Badge key={activite}>{activite}</Badge>
          ))}
        </span>
      ),
    },
    {
      cle: 'du',
      entete: 'Cotisations dues',
      classe: 'text-right',
      valeurTri: (l) => l.du_inscriptions,
      rendu: (l) => <span className="tabulaire">{formaterMontant(l.du_inscriptions)}</span>,
    },
    {
      cle: 'adhesion',
      entete: 'Adhésion',
      classe: 'text-right',
      valeurTri: (l) => l.du_adhesion,
      rendu: (l) => (
        <span className="tabulaire text-encre-500">
          {l.du_adhesion > 0 ? formaterMontant(l.du_adhesion) : '—'}
        </span>
      ),
    },
    {
      cle: 'regle',
      entete: 'Réglé',
      classe: 'text-right',
      valeurTri: (l) => l.regle,
      rendu: (l) => <span className="tabulaire">{formaterMontant(l.regle)}</span>,
    },
    {
      cle: 'ecart',
      // « Reste du » (2026-10-13), signe conserve : negatif il reste a
      // payer, positif on a trop recu.
      entete: 'Reste dû',
      classe: 'text-right',
      valeurTri: (l) => l.ecart,
      // Rouge pour un reste a payer, VERT pour un trop-percu (2026-09-29) :
      // la convention de la fiche adherent, que l'ambre contredisait.
      rendu: (l) =>
        l.ecart === 0 ? (
          <span className="tabulaire text-encre-400">0,00 €</span>
        ) : (
          <span
            className={`tabulaire font-medium ${l.ecart < 0 ? 'text-red-700' : 'text-emerald-700'}`}
          >
            {l.ecart > 0 ? '+' : ''}
            {formaterMontant(l.ecart)}
          </span>
        ),
    },
    // « Relancer » sur chaque reste a payer : ouvre Gmail avec le courriel
    // de relance prerempli. La cellule arrete le clic, qui ouvre sinon la
    // fiche.
    ...(peut('message.envoyer')
      ? [
          {
            cle: 'relance',
            entete: '',
            classe: 'text-right',
            rendu: (l: LigneRapprochement) =>
              l.ecart < 0 ? (
                <Infobulle texte="Ouvre dans Gmail un courriel de relance prérempli" className="inline-flex">
                  <button
                    type="button"
                    className="text-xs underline disabled:pointer-events-none"
                    disabled={relanceEnCours !== null}
                    onClick={(e) => {
                      e.stopPropagation();
                      void relancer(l);
                    }}
                  >
                    {relanceEnCours === l.personne_id ? 'Préparation…' : 'Relancer'}
                  </button>
                </Infobulle>
              ) : null,
          } satisfies Colonne<LigneRapprochement>,
        ]
      : []),
  ];

  const triees = trierLignes(liste, colonnes, tri);

  const exporter = () =>
    exporterTableau(
      'rapprochement',
      ['Adhérent', 'Activités', 'Type', 'Cotisations dues', 'Adhésion', 'Réglé', 'Reste dû'],
      triees.map((l) => [
        `${l.nom.toUpperCase()} ${l.prenom}`,
        l.activites.join(' · '),
        LIBELLE_TYPE_MEMBRE[l.type_membre],
        montantExport(l.du_inscriptions),
        montantExport(l.du_adhesion),
        montantExport(l.regle),
        montantExport(l.ecart),
      ]),
    );

  return (
    <>
      {/* Les trois tuiles (equilibres, avec ecart, ecart global) ont ete
          retirees le 2026-10-13 : le total du des lignes affichees, a
          cote du compte, dit ce qui compte. */}
      <div className="mb-3 flex flex-wrap items-center gap-3 text-sm text-encre-600">
        <label className="inline-flex items-center gap-2">
          <input
            type="checkbox"
            checked={seulementEcarts}
            onChange={(e) => setSeulementEcarts(e.target.checked)}
          />
          N’afficher que les écarts
        </label>

        <ChoixMultiple
          libelle="Activité"
          options={activitesProposees.map(([id, libelle]) => ({ valeur: id, libelle }))}
          valeurs={filtres.activites}
          onChanger={(valeurs) => poserFiltre('activites', valeurs)}
        />
        <ChoixMultiple
          libelle="Jour"
          options={joursProposes.map((jour) => ({ valeur: String(jour), libelle: libelleJour(jour) }))}
          valeurs={filtres.jours}
          onChanger={(valeurs) => poserFiltre('jours', valeurs)}
        />
        <ChoixMultiple
          libelle="Professeur"
          options={encadrantsProposes.map((e) => ({ valeur: e.personnel_id, libelle: libelleEncadrant(e) }))}
          valeurs={filtres.professeurs}
          onChanger={(valeurs) => poserFiltre('professeurs', valeurs)}
        />
        <ChoixMultiple
          libelle="Type"
          options={(['actif', 'usager', 'inactif'] as const).map((t) => ({
            valeur: t,
            libelle: LIBELLE_TYPE_MEMBRE[t],
          }))}
          valeurs={types}
          onChanger={setTypes}
        />

        <span className="text-xs">
          <span className="tabulaire font-medium text-encre-800">{liste.length}</span> ligne(s) pour
          un total dû de{' '}
          <span
            className={`tabulaire font-medium ${totalDu < 0 ? 'text-red-700' : totalDu > 0 ? 'text-emerald-700' : 'text-encre-800'}`}
          >
            {totalDu > 0 ? '+' : ''}
            {formaterMontant(totalDu)}
          </span>
        </span>
        {liste.length > 0 && (
          <span className="ml-auto flex items-center gap-2">
            {/* « Ecrire a la selection » (2026-10-13) : les adherents des
                lignes affichees, comme sur Adherents — pour relancer
                ceux d'un cours qui n'ont pas solde. */}
            {peut('message.envoyer') && (
              <BoutonGmail
                libelle="Écrire aux adhérents affichés"
                onClick={() => setEcrireA(triees.map((l) => l.personne_id))}
              />
            )}
            <BoutonExcel libelle="Exporter le rapprochement (Excel)" onClick={() => void exporter()} />
          </span>
        )}
      </div>

      {avisRelance && (
        <p className="mb-3 rounded-md border border-amber-200 bg-amber-50 p-3 text-sm text-amber-900" role="alert">
          {avisRelance}
        </p>
      )}

      <div className="carte">
        {/* Un ecart se comprend sur la fiche : quelles inscriptions, quels
            reglements. La ligne y mene, comme sur les trois listes. */}
        <Tableau
          colonnes={colonnes}
          lignes={triees}
          cleDe={(l) => l.personne_id}
          messageVide="Tout est rapproché : aucun écart."
          onLigneCliquee={(l) => naviguer(`/adherents/${l.personne_id}`)}
          tri={tri}
          onTrier={setTri}
        />
      </div>

      <p className="mt-4 text-xs text-encre-400">
        Un écart négatif est un reste à payer, un écart positif un trop-perçu. Les règlements versés
        au nom d’une personne sans aucune inscription n’apparaissent pas ici : ils restent non
        affectés, visibles dans l’onglet Règlements.
      </p>

      <CommunicationGroupee
        ouverte={ecrireA !== null}
        onFermer={() => setEcrireA(null)}
        source={ecrireA ? { personneIds: ecrireA } : null}
        origine={
          ecrireA
            ? `Les ${ecrireA.length} adhérent(s) du rapprochement affiché, et leurs contacts — ${
                seulementEcarts ? 'écarts seulement' : 'toutes les lignes'
              }${
                [
                  ...filtres.activites.map((id) => activitesProposees.find(([x]) => x === id)?.[1]),
                  ...filtres.jours.map((j) => libelleJour(Number(j))),
                  ...filtres.professeurs.map(
                    (id) => encadrantsProposes.find((e) => e.personnel_id === id)?.prenom_usuel,
                  ),
                  ...types.map((t) => LIBELLE_TYPE_MEMBRE[t as LigneRapprochement['type_membre']]),
                ]
                  .filter(Boolean)
                  .map((f) => `, ${f}`)
                  .join('')
              }`
            : ''
        }
      />
    </>
  );
}

/**
 * Ce qu'un adherent doit, dans la ventilation d'un reglement.
 *
 * Chaque adherent charge ses propres lignes : c'est ce qui permet d'en
 * empiler plusieurs sur un meme versement (RG-15) sans que le formulaire
 * ait a orchestrer N requetes lui-meme.
 */
function LignesAdherent({
  personne,
  anneeId,
  parts,
  partsInitiales,
  onLignes,
  onChanger,
  onRetirer,
}: {
  personne: Personne;
  anneeId: string;
  parts: Record<string, string>;
  /**
   * En MODIFICATION, la part que CE reglement couvre deja, par cle.
   * `montant_regle` la compte : sans la reintegrer, rouvrir un cheque de
   * 1 000 EUR afficherait « reste a payer 0 » sur les lignes qu'il paie,
   * et la proposition serait nulle.
   */
  partsInitiales: Record<string, number>;
  /**
   * Publie les lignes de cet adherent au formulaire, dans l'ordre
   * d'affichage. C'est ce qui permet a la proposition de se deverser en
   * CONTINU d'un adherent au suivant (RG-15, le cheque familial) : le
   * parent seul connait l'ordre des beneficiaires et l'enveloppe.
   */
  onLignes: (personneId: string, lignes: { cle: string; reste: number }[]) => void;
  /**
   * Mise a jour FONCTIONNELLE, et non une valeur : plusieurs adherents
   * partagent le meme etat. Passer `{ ...parts, x }` repartirait de la
   * copie figee au rendu, et deux modifications rapprochees se
   * perdraient l'une l'autre — vecu avec deux « tout ce qu'il doit »
   * cliques coup sur coup.
   */
  onChanger: (maj: (actuel: Record<string, string>) => Record<string, string>) => void;
  onRetirer: () => void;
}) {
  const inscriptions = useRessource(
    () => api.get<Inscription[]>('/api/inscriptions', { anneeId, personneId: personne.id }),
    [personne.id, anneeId],
  );
  const adhesions = useRessource(
    () => api.get<Adhesion[]>('/api/adhesions', { anneeId, personneId: personne.id }),
    [personne.id, anneeId],
  );

  const listeI = inscriptions.donnees ?? [];
  const listeA = adhesions.donnees ?? [];

  const cleI = (id: string) => `${personne.id}|inscription:${id}`;
  const cleA = (id: string) => `${personne.id}|adhesion:${id}`;

  /**
   * DEUX valeurs, a ne pas confondre.
   *
   * La CAPACITE est ce que la ligne peut encore absorber : le du, moins
   * ce que d'AUTRES reglements couvrent. La part de celui qu'on modifie
   * est reintegree — sinon il faudrait la retrancher deux fois. Elle ne
   * bouge pas pendant la saisie, et c'est elle qui alimente la cascade :
   * la faire dependre du champ rendrait la proposition circulaire.
   */
  const capacite = (cle: string, du: number, regle: number) =>
    Math.max(du - regle + (partsInitiales[cle] ?? 0), 0);

  /** Ce que le champ porte a l'instant, en centimes. */
  const saisi = (cle: string) => {
    const n = Math.round(Number.parseFloat((parts[cle] ?? '').replace(',', '.')) * 100);
    return Number.isFinite(n) ? n : 0;
  };

  /**
   * Le RESTE AFFICHE, recalcule a chaque frappe : la capacite moins ce
   * qu'on est en train d'affecter. C'est le « montant du − somme des
   * allocations » demande le 2026-09-01 — on voit fondre le reste a
   * mesure qu'on repartit, au lieu d'un chiffre fige a l'ouverture.
   *
   * Plancher a zero : « reste a payer » ne peut pas etre negatif. Un
   * depassement se lit au pied de la ventilation, qui refuse alors
   * l'enregistrement.
   */
  const resteAffiche = (cle: string, du: number, regle: number) =>
    Math.max(capacite(cle, du, regle) - saisi(cle), 0);

  // Les lignes remontent au formulaire des qu'elles sont connues, dans
  // l'ordre affiche : inscriptions puis adhesion. Elles portent la
  // CAPACITE — pas le reste affiche, qui varie a chaque frappe et ferait
  // republier la liste sans cesse.
  const lignes = [
    ...listeI.map((i) => ({ cle: cleI(i.id), reste: capacite(cleI(i.id), i.montant_du, i.montant_regle) })),
    ...listeA.map((a) => ({ cle: cleA(a.id), reste: capacite(cleA(a.id), a.montant, a.montant_regle) })),
  ];
  const signature = lignes.map((l) => `${l.cle}:${l.reste}`).join('|');

  /**
   * La publication passe par un EFFET, et non par le rendu.
   *
   * Poser l'etat du PARENT pendant le rendu de l'enfant n'est pas garanti
   * par React : la mise a jour peut ne jamais aboutir. Constate le
   * 2026-09-01 — les deux blocs affichaient bien leurs restes, mais la
   * proposition ignorait le second adherent, ses lignes n'etant jamais
   * parvenues au formulaire.
   *
   * `signature` resume les lignes : c'est le seul declencheur utile. La
   * callback passe par une reference pour que sa nouvelle identite a
   * chaque rendu du parent ne relance pas l'effet en boucle.
   */
  const publier = useRef(onLignes);
  publier.current = onLignes;
  // Les LIGNES passent par une reference, la signature ne sert qu'a
  // declencher. Les reconstruire en decoupant la signature etait un
  // piege : les cles portent elles-memes un « | » — `personneId|cible:id`
  // —, donc le decoupage les brisait. Symptome trompeur : le total
  // ventile tombait juste, mais aucun champ ne se remplissait, les cles
  // produites ne correspondant a aucun.
  const aPublier = useRef(lignes);
  aPublier.current = lignes;
  const pret = !inscriptions.chargement && !adhesions.chargement;
  useEffect(() => {
    if (!pret) return;
    publier.current(personne.id, aPublier.current);
  }, [pret, personne.id, signature]);

  return (
    <div className="mt-2 rounded-md bg-encre-50 p-2">
      <div className="flex items-center justify-between gap-2">
        <span className="text-sm font-medium">
          {personne.nom.toUpperCase()} {personne.prenom}
        </span>
        {/* « Tout ce qu'il doit » a ete retire (2026-09-01) : la
            proposition automatique le remplace, et le bouton pre-remplissait
            avec le montant du ENTIER — il proposait donc de repayer ce qui
            l'etait deja. */}
        <button type="button" className="text-xs text-red-600 underline" onClick={onRetirer}>
          Retirer
        </button>
      </div>

      <ul className="mt-2 space-y-1.5">
        {listeI.map((i) => (
          <li key={i.id} className="flex items-center gap-2 text-sm">
            <span className="min-w-0 flex-1 truncate">{i.activite_libelle}</span>
            <span className="tabulaire text-xs text-encre-500">
              reste à payer {formaterMontant(resteAffiche(cleI(i.id), i.montant_du, i.montant_regle))}
            </span>
            <input
              className="champ w-24 bg-white py-1 text-right"
              placeholder="0,00"
              value={parts[`${personne.id}|inscription:${i.id}`] ?? ''}
              onChange={(e) =>
                onChanger((actuel) => ({
                  ...actuel,
                  [`${personne.id}|inscription:${i.id}`]: e.target.value,
                }))
              }
            />
          </li>
        ))}
        {listeA.map((a) => (
          <li key={a.id} className="flex items-center gap-2 text-sm">
            <span className="min-w-0 flex-1 truncate">Adhésion</span>
            <span className="tabulaire text-xs text-encre-500">
              reste à payer {formaterMontant(resteAffiche(cleA(a.id), a.montant, a.montant_regle))}
            </span>
            <input
              className="champ w-24 bg-white py-1 text-right"
              placeholder="0,00"
              value={parts[`${personne.id}|adhesion:${a.id}`] ?? ''}
              onChange={(e) =>
                onChanger((actuel) => ({
                  ...actuel,
                  [`${personne.id}|adhesion:${a.id}`]: e.target.value,
                }))
              }
            />
          </li>
        ))}
        {!inscriptions.chargement && listeI.length === 0 && listeA.length === 0 && (
          <li className="text-xs text-amber-700">
            Aucune inscription ni adhésion sur cette année : rien à couvrir pour cet adhérent.
          </li>
        )}
      </ul>
    </div>
  );
}

/**
 * Saisie d'un reglement, avec l'assistant d'echelonnement (RG-14).
 *
 * La ventilation se fait en choisissant un adherent : ses inscriptions et
 * son adhesion s'affichent avec le reste a payer, et le montant se
 * repartit dessus. C'est ce qui rattache le versement a ce qu'il paie.
 */
/**
 * Exporte : la fiche d'un adherent ouvre le MEME formulaire, avec la
 * personne deja posee. Deux saisies de reglement divergeraient au
 * premier changement de regle.
 */
export function FormulaireReglement({
  ouverte,
  anneeId,
  anneeLibelle,
  personneInitiale = null,
  reglement = null,
  onFermer,
  onEnregistre,
}: {
  ouverte: boolean;
  anneeId: string;
  anneeLibelle: string;
  /** Beneficiaire pre-selectionne — la fiche adherent le connait deja. */
  personneInitiale?: Personne | null;
  /**
   * Reglement a MODIFIER. Absent, le formulaire cree — c'est son usage
   * historique. Present, il pre-remplit tout, ventilation comprise, et
   * envoie un PATCH.
   */
  reglement?: Reglement | null;
  onFermer: () => void;
  onEnregistre: () => void;
}) {
  // Calculee ici et non plus bas : l'amorce du formulaire s'en sert pour
  // dater la reception du jour.
  const aujourdHui = new Intl.DateTimeFormat('fr-CA', { timeZone: 'Europe/Paris' }).format(
    new Date(),
  );

  const [valeurs, setValeurs] = useState({
    mode: 'cheque' as ModeReglement,
    montant: '',
    dateReception: '',
    dateEncaissement: '',
    emetteurNom: '',
    banque: '',
    numeroCheque: '',
    commentaire: '',
  });
  const [saisiePersonne, setSaisiePersonne] = useState('');
  // RG-15 : un meme reglement couvre plusieurs adherents. Le cheque SG
  // 0000258 paie SORIANO ET HEBERT ; la saisie doit pouvoir le dire.
  const [personnes, setPersonnes] = useState<Personne[]>([]);
  const [parts, setParts] = useState<Record<string, string>>({});
  /** En modification : ce que CE reglement couvre deja, en centimes. */
  const [partsInitiales, setPartsInitiales] = useState<Record<string, number>>({});
  /** Les lignes de chaque adherent, publiees par `LignesAdherent`. */
  const [lignesParPersonne, setLignesParPersonne] = useState<
    Record<string, { cle: string; reste: number }[]>
  >({});
  const [erreur, setErreur] = useState<string | null>(null);
  const [info, setInfo] = useState<string | null>(null);
  const [enCours, setEnCours] = useState(false);
  const [doublonConfirme, setDoublonConfirme] = useState(false);
  // « Enregistrer & dupliquer » : le drapeau est pose par le bouton
  // clique, AVANT que la soumission ne parte.
  const dupliquer = useRef(false);
  // Des reglements ont ete enregistres pendant que la modale restait
  // ouverte : la fermeture doit rafraichir les listes, pas les ignorer.
  const [enregistres, setEnregistres] = useState(0);

  const edition = reglement !== null;
  // LECTURE SEULE (2026-10-07) : un reglement verrouille s'ouvre pour
  // etre lu — ventilation comprise —, pas pour etre modifie. La regle
  // vit dans lib/reglement.ts, le serveur la tient de son cote.
  const { peut: peutLecture } = useAuth();
  const motifVerrou = reglement ? motifLectureSeule(reglement, peutLecture) : null;
  const lectureSeule = motifVerrou !== null;

  // Amorce a l'OUVERTURE : la personne pre-selectionnee entre dans la
  // liste des beneficiaires comme si on l'avait cherchee. Une seule fois
  // par ouverture — sans quoi retirer la personne la ferait revenir.
  const [amorcePour, setAmorcePour] = useState<string | null>(null);
  const cleAmorce = ouverte ? (reglement?.id ?? personneInitiale?.id ?? 'aucune') : null;
  if (cleAmorce && cleAmorce !== amorcePour) {
    setAmorcePour(cleAmorce);
    setPersonnes(personneInitiale ? [personneInitiale] : []);
    setParts({});
    setPartsInitiales({});
    setLignesParPersonne({});
    setErreur(null);
    setInfo(null);
    setDoublonConfirme(false);
    if (reglement) {
      setValeurs({
        mode: reglement.mode,
        montant: (reglement.montant / 100).toFixed(2),
        dateReception: reglement.date_reception,
        dateEncaissement: reglement.date_encaissement,
        emetteurNom: reglement.emetteur_nom ?? '',
        banque: reglement.banque ?? '',
        numeroCheque: reglement.numero_cheque ?? '',
        commentaire: reglement.commentaire ?? '',
      });
    } else {
      setValeurs({
        mode: 'cheque',
        montant: '',
        // La reception est datee du JOUR : on saisit ce qu'on vient de
        // recevoir. L'encaissement la suit tant qu'on n'en decide pas
        // autrement.
        dateReception: aujourdHui,
        dateEncaissement: aujourdHui,
        emetteurNom: '',
        banque: '',
        numeroCheque: '',
        commentaire: '',
      });
    }
  }
  if (!ouverte && amorcePour !== null) setAmorcePour(null);

  // La VENTILATION existante se recharge a part : la liste des reglements
  // ne la porte pas, elle n'en donne que le total et les noms. Sans elle,
  // enregistrer une modification remplacerait la ventilation par ce que
  // l'ecran affiche — c'est-a-dire rien.
  const detail = useRessource(
    () =>
      ouverte && reglement
        ? api.get<{ ventilation: LigneVentilation[] }>(`/api/reglements/${reglement.id}`)
        : Promise.resolve(null),
    [ouverte, reglement?.id ?? ''],
  );

  const [ventilationChargee, setVentilationChargee] = useState<string | null>(null);
  if (reglement && detail.donnees && ventilationChargee !== reglement.id) {
    setVentilationChargee(reglement.id);
    const lignes = detail.donnees.ventilation;
    // Les beneficiaires se deduisent de la ventilation : une personne y
    // figure autant de fois qu'elle a de lignes, on ne la garde qu'une.
    const parPersonne = new Map<string, Personne>();
    const nouvellesParts: Record<string, string> = {};
    for (const ligne of lignes) {
      parPersonne.set(ligne.personne_id, {
        id: ligne.personne_id,
        nom: ligne.personne_nom,
        prenom: ligne.personne_prenom,
      } as Personne);
      nouvellesParts[`${ligne.personne_id}|${ligne.cible}:${ligne.cibleId}`] = (
        ligne.montant / 100
      ).toFixed(2);
    }
    setPersonnes([...parPersonne.values()]);
    setParts(nouvellesParts);
    setPartsInitiales(
      Object.fromEntries(
        lignes.map((l) => [`${l.personne_id}|${l.cible}:${l.cibleId}`, l.montant]),
      ),
    );
  }
  if (!ouverte && ventilationChargee !== null) setVentilationChargee(null);

  const rechercheDifferee = useDelai(saisiePersonne, 300);
  const candidats = useRessource(
    () =>
      ouverte && rechercheDifferee.length >= 2
        ? api.get<Personne[]>('/api/personnes', { recherche: rechercheDifferee, limite: 8 })
        : Promise.resolve([]),
    [ouverte, rechercheDifferee],
  );

  // Les candidats deja retenus sortent de la liste : la meme personne ne
  // se choisit pas deux fois. Le clavier travaille sur cette liste-la.
  const proposables = (candidats.donnees ?? []).filter(
    (c) => !personnes.some((p) => p.id === c.id),
  );
  const ajouter = (p: Personne) => {
    setPersonnes([...personnes, p]);
    setSaisiePersonne('');
  };
  const clavier = useListeClavier(proposables, ajouter);

  const montantCentimes = Math.round(Number.parseFloat(valeurs.montant.replace(',', '.')) * 100);
  const montantValide = Number.isFinite(montantCentimes) && montantCentimes > 0;

  const totalVentile = Object.values(parts).reduce((somme, brut) => {
    const n = Math.round(Number.parseFloat(brut.replace(',', '.')) * 100);
    return somme + (Number.isFinite(n) ? n : 0);
  }, 0);

  /**
   * LA PROPOSITION, a la saisie du montant (decision du 2026-09-01).
   *
   * On descend les lignes dans l'ordre AFFICHE — adherents dans l'ordre
   * d'ajout, puis inscriptions et adhesion — et chacune recoit le
   * minimum entre son reste a payer et ce qui subsiste de l'enveloppe.
   * Sur 1 000 EUR pour un piano a 900 et un solfege a 400 : 900 puis 100.
   *
   * Elle PROPOSE, elle n'impose pas : l'utilisateur redistribue ensuite
   * ligne a ligne, et rien ne recalcule derriere lui. Seule une nouvelle
   * saisie du montant repose une proposition — c'est le geste qui la
   * declenche, et il annonce clairement qu'on repart de zero.
   *
   * Un paiement superieur au reste total laisse le surplus NON AFFECTE :
   * la somme des propositions ne peut pas depasser ce qui est du. Le pied
   * de ventilation le dit deja, et la sauvegarde reste possible.
   */
  const proposer = (montantTexte: string) => {
    const enveloppeTotale = Math.round(Number.parseFloat(montantTexte.replace(',', '.')) * 100);
    if (!Number.isFinite(enveloppeTotale) || enveloppeTotale <= 0) return;

    let reste = enveloppeTotale;
    const proposition: Record<string, string> = {};
    for (const personne of personnes) {
      for (const ligne of lignesParPersonne[personne.id] ?? []) {
        const part = Math.min(ligne.reste, reste);
        if (part > 0) {
          // Virgule et non point : l'interface est en francais, et la
          // soumission accepte les deux.
          proposition[ligne.cle] = (part / 100).toFixed(2).replace('.', ',');
          reste -= part;
        }
      }
    }
    setParts(proposition);
  };

  const soumettre = async (evenement: FormEvent) => {
    evenement.preventDefault();
    setErreur(null);

    // Un reglement paie quelque chose. Sans beneficiaire, l'argent
    // n'apparait ni au rapprochement ni sur une fiche : le serveur le
    // refuse desormais, l'ecran le dit avant d'envoyer.
    if (personnes.length === 0) {
      setErreur('Choisissez l’adhérent qui paie.');
      return;
    }
    if (totalVentile <= 0) {
      setErreur('Indiquez ce que ce règlement paie : au moins une inscription ou une adhésion.');
      return;
    }
    // Affecter plus que le montant reviendrait a payer avec de l'argent
    // qui n'existe pas. En deca, en revanche, on enregistre : un versement
    // partiellement affecte est un cas legitime, signale par un tag dans
    // la liste. Le serveur tient la meme regle — a la creation par le
    // schema, a la modification par la route.
    if (totalVentile > montantCentimes) {
      setErreur(
        `L’affectation (${formaterMontant(totalVentile)}) dépasse le montant du règlement ` +
          `(${formaterMontant(montantCentimes)}).`,
      );
      return;
    }

    setEnCours(true);
    try {
      // Cle : « <personneId>|<cible>:<cibleId> ». Le prefixe ne sert qu'a
      // l'ecran — l'API, elle, ne connait que ce qui est paye.
      const ventilations = Object.entries(parts)
        .map(([cle, brut]) => {
          const [cible, cibleId] = cle.slice(cle.indexOf('|') + 1).split(':');
          const montant = Math.round(Number.parseFloat(brut.replace(',', '.')) * 100);
          return { cible: cible as 'inscription' | 'adhesion', cibleId: cibleId!, montant };
        })
        .filter((v) => Number.isFinite(v.montant) && v.montant > 0);

      const corps = {
        anneeId,
        mode: valeurs.mode,
        montant: montantCentimes,
        dateReception: valeurs.dateReception,
        // Le champ n'existe que pour un cheque : ailleurs, le serveur
        // aligne l'encaissement sur la reception.
        dateEncaissement: valeurs.mode === 'cheque' ? valeurs.dateEncaissement : undefined,
        emetteurNom: valeurs.emetteurNom || null,
        banque: valeurs.banque || null,
        numeroCheque: valeurs.numeroCheque || null,
        doublonConfirme,
        commentaire: valeurs.commentaire || null,
        ventilations,
      };

      if (reglement) {
        await api.patch(`/api/reglements/${reglement.id}`, corps);
        onEnregistre();
        return;
      }

      await api.post('/api/reglements', corps);

      if (dupliquer.current) {
        // La copie reste a l'ecran, identique : on ajuste ce qui change
        // (date, numero...) puis on enregistre. Le serveur signalera tout
        // doublon exact et demandera confirmation — chaque enregistrement
        // reprend sa propre confirmation.
        setEnregistres((n) => n + 1);
        setDoublonConfirme(false);
        setInfo(
          'Règlement enregistré. Sa copie est prête : ajustez la date ou le n° de chèque si besoin, puis enregistrez.',
        );
      } else {
        onEnregistre();
      }
    } catch (cause) {
      const message = cause instanceof ErreurApi ? cause.message : 'Enregistrement impossible.';
      setErreur(message);
      setInfo(null);
      // RG-12 : le refus pour doublon est levable par confirmation.
      if (message.includes('existe deja')) setDoublonConfirme(false);
    } finally {
      setEnCours(false);
    }
  };

  // Apres un « Enregistrer & dupliquer », la fermeture doit rafraichir
  // les listes : des reglements existent deja.
  const fermer = () => (enregistres > 0 ? onEnregistre() : onFermer());

  return (
    <Modale
      titre={lectureSeule ? 'Détail du règlement' : edition ? 'Modifier le règlement' : 'Saisir un règlement'}
      ouverte={ouverte}
      onFermer={fermer}
    >
      <form onSubmit={soumettre} className="space-y-4">
        {motifVerrou && (
          <p className="rounded-md border border-encre-200 bg-encre-50 p-3 text-sm text-encre-700">
            {motifVerrou}
          </p>
        )}
        {/* `fieldset disabled` neutralise TOUS les champs et boutons qu'il
            contient, ventilation comprise — un garde par champ en aurait
            oublie un. */}
        <fieldset disabled={lectureSeule} className="space-y-4 disabled:opacity-70">
        <Champ libelle="Année scolaire" aide="Celle affichée à l’écran.">
          <input className="champ bg-encre-50 text-encre-600" value={anneeLibelle} readOnly />
        </Champ>

        <div className="grid grid-cols-2 gap-3">
          <Champ libelle="Mode">
            <select
              className="champ"
              value={valeurs.mode}
              onChange={(e) => setValeurs({ ...valeurs, mode: e.target.value as ModeReglement })}
            >
              {MODES.map((m) => (
                <option key={m.code} value={m.code}>
                  {m.libelle}
                </option>
              ))}
            </select>
          </Champ>
          <Champ libelle="Montant total (€)">
            <input
              className="champ"
              value={valeurs.montant}
              onChange={(e) => {
                setValeurs({ ...valeurs, montant: e.target.value });
                proposer(e.target.value);
              }}
              placeholder="950,00"
              required
            />
          </Champ>
        </div>

        <div className="grid grid-cols-2 gap-3">
          <Champ libelle="Date de réception" aide="Le jour où l’école a reçu le paiement.">
            <input
              className="champ"
              type="date"
              value={valeurs.dateReception}
              // On ne reçoit pas demain, quel que soit le mode.
              max={aujourdHui}
              onChange={(e) => {
                // Tant que l'encaissement suit la réception, il la suit
                // encore : sans cela, corriger la réception laisserait
                // une date d'encaissement antérieure, silencieusement.
                const suivait = valeurs.dateEncaissement === valeurs.dateReception;
                setValeurs({
                  ...valeurs,
                  dateReception: e.target.value,
                  dateEncaissement: suivait ? e.target.value : valeurs.dateEncaissement,
                });
              }}
              required
            />
          </Champ>

          {/* La date d'encaissement ne se saisit que pour un CHÈQUE : il
              se dépose plus tard. Tout autre mode se constate — l'argent
              tombe le jour où on le reçoit, et le serveur l'aligne. */}
          {valeurs.mode === 'cheque' && (
            <Champ
              libelle="Date d’encaissement"
              aide="Quand ce chèque doit partir en banque. Elle peut être future."
            >
              <input
                className="champ"
                type="date"
                value={valeurs.dateEncaissement}
                onChange={(e) => setValeurs({ ...valeurs, dateEncaissement: e.target.value })}
                required
              />
            </Champ>
          )}
        </div>

        {/* Changer la date d'encaissement d'un chèque déjà en remise l'en
            sort : sa nouvelle date n'y a plus sa place. Le dire avant
            d'enregistrer, pas après. */}
        {edition &&
          reglement?.remise_reference &&
          valeurs.dateEncaissement !== reglement.date_encaissement && (
            <p className="rounded-md border border-amber-200 bg-amber-50 p-3 text-xs text-amber-900">
              Ce chèque sortira de la remise {reglement.remise_reference} : sa nouvelle date
              d’encaissement n’y correspond plus. Il redeviendra « prévu » et sera repris à la
              prochaine sélection.
            </p>
          )}

        {valeurs.mode === 'cheque' && (
          <div className="grid grid-cols-3 gap-3">
            <Champ libelle="Émetteur" aide="Obligatoire (RG-11).">
              <input
                className="champ"
                value={valeurs.emetteurNom}
                onChange={(e) => setValeurs({ ...valeurs, emetteurNom: e.target.value })}
                required
              />
            </Champ>
            <Champ libelle="Banque">
              <input
                className="champ"
                value={valeurs.banque}
                onChange={(e) => setValeurs({ ...valeurs, banque: e.target.value })}
              />
            </Champ>
            <Champ libelle="N° de chèque">
              <input
                className="champ"
                value={valeurs.numeroCheque}
                onChange={(e) => setValeurs({ ...valeurs, numeroCheque: e.target.value })}
              />
            </Champ>
          </div>
        )}

        {/* --- Ventilation : ce que le reglement paie ------------------- */}
        <div className="rounded-md border border-encre-200 p-3">
          <div className="etiquette">Ce que ce règlement paie</div>

          {personnes.map((p) => (
            <LignesAdherent
              key={p.id}
              personne={p}
              anneeId={anneeId}
              parts={parts}
              partsInitiales={partsInitiales}
              onLignes={(personneId, lignes) =>
                setLignesParPersonne((actuel) => ({ ...actuel, [personneId]: lignes }))
              }
              onChanger={setParts}
              onRetirer={() => {
                setPersonnes(personnes.filter((autre) => autre.id !== p.id));
                // Les parts de l'adherent retire partent avec lui : sinon
                // elles resteraient dans le total sans plus rien couvrir.
                // C'est ce que le prefixe de cle permet de faire.
                setParts((actuel) =>
                  Object.fromEntries(
                    Object.entries(actuel).filter(([cle]) => !cle.startsWith(`${p.id}|`)),
                  ),
                );
              }}
            />
          ))}

          <div className={personnes.length > 0 ? 'mt-3 border-t border-encre-100 pt-3' : 'mt-1'}>
            <input
              className="champ"
              placeholder={
                personnes.length > 0 ? 'Ajouter un autre adhérent…' : 'Chercher un adhérent…'
              }
              value={saisiePersonne}
              onChange={(e) => setSaisiePersonne(e.target.value)}
              onKeyDown={clavier.auClavier}
            />
            {proposables.length > 0 && (
              <ul
                ref={clavier.liste}
                className="mt-2 max-h-40 divide-y divide-encre-100 overflow-y-auto rounded-md border border-encre-200"
              >
                {proposables.map((p, rang) => (
                    <li key={p.id} data-index={rang}>
                      <button
                        type="button"
                        className={`w-full px-3 py-2 text-left text-sm ${
                          rang === clavier.index ? CLASSE_SURVOL : ''
                        }`}
                        onMouseEnter={() => clavier.survoler(rang)}
                        onClick={() => ajouter(p)}
                      >
                        {p.nom.toUpperCase()} {p.prenom}
                      </button>
                    </li>
                  ))}
              </ul>
            )}
          </div>

          {personnes.length === 0 && (
            <p className="mt-2 text-xs text-encre-400">
              Un même règlement peut couvrir plusieurs adhérents — un chèque familial, par exemple.
              Ajoutez-les l’un après l’autre.
            </p>
          )}

          {montantValide && personnes.length > 0 && (
            <p className="mt-3 text-xs text-encre-500">
              Ventilé : <span className="tabulaire">{formaterMontant(totalVentile)}</span> sur{' '}
              <span className="tabulaire">{formaterMontant(montantCentimes)}</span>
              {totalVentile > montantCentimes && (
                <span className="ml-2 text-red-700">la ventilation dépasse le montant</span>
              )}
              {totalVentile < montantCentimes && (
                <span className="ml-2 text-amber-700">
                  {formaterMontant(montantCentimes - totalVentile)} non affectés
                </span>
              )}
            </p>
          )}
        </div>

        <Champ libelle="Commentaire">
          <input
            className="champ"
            value={valeurs.commentaire}
            onChange={(e) => setValeurs({ ...valeurs, commentaire: e.target.value })}
          />
        </Champ>

        {erreur?.includes('existe deja') && (
          <label className="flex items-start gap-2 rounded-md border border-amber-200 bg-amber-50 p-3 text-xs text-amber-800">
            <input
              type="checkbox"
              checked={doublonConfirme}
              onChange={(e) => setDoublonConfirme(e.target.checked)}
            />
            <span>
              Je confirme qu’il s’agit bien d’un second règlement distinct, à enregistrer malgré ce
              doublon (RG-12 : signalé, jamais interdit).
            </span>
          </label>
        )}

        </fieldset>

        {erreur && (
          <p
            className="rounded-md border border-red-200 bg-red-50 p-3 text-sm text-red-800"
            role="alert"
          >
            {erreur}
          </p>
        )}
        {!erreur && info && (
          <p className="rounded-md border border-emerald-200 bg-emerald-50 p-3 text-sm text-emerald-800">
            {info}
          </p>
        )}
        <div className="flex justify-end gap-2 pt-2">
          <button type="button" className="bouton-secondaire" onClick={fermer}>
            {lectureSeule || enregistres > 0 ? 'Fermer' : 'Annuler'}
          </button>
          {lectureSeule ? null : (
          <>
          {/* Le drapeau se pose AU CLIC, avant la soumission : les deux
              boutons partagent le meme formulaire et sa validation.
              Dupliquer n'a pas de sens en modification — on retouche une
              ligne existante, on n'en fabrique pas une seconde. */}
          {!edition && (
            <button
              type="submit"
              className="bouton-secondaire"
              disabled={enCours}
              onClick={() => {
                dupliquer.current = true;
              }}
            >
              Enregistrer &amp; dupliquer
            </button>
          )}
          <button
            type="submit"
            className="bouton-principal"
            disabled={enCours}
            onClick={() => {
              dupliquer.current = false;
            }}
          >
            {enCours ? 'Enregistrement…' : 'Enregistrer'}
          </button>
          </>
          )}
        </div>
      </form>
    </Modale>
  );
}
